import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useNavigate } from "react-router-dom";

import { apiClient } from "../api/client";
import type { CatalogListResponse } from "../api/types";
import { ProductCard } from "../components/ProductCard";
import { Testimonials } from "../components/Testimonials";
import { EmptyState } from "../components/EmptyState";
import { CatalogSkeleton } from "../components/LoadingSkeleton";
import { getSuiteBySlug } from "../data/suites";

/**
 * Cycle 8 STORY-702: Suite landing page.
 *
 * Public route /suite/:name (no auth required — marketing page).
 * Hero + value props + use cases + product list + testimonials.
 */
export function SuiteLandingPage(): ReactElement {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const suite = name ? getSuiteBySlug(name) : undefined;

  const productsQuery = useQuery<CatalogListResponse>({
    queryKey: ["catalog-suite", suite?.apiSuite],
    queryFn: async () => {
      if (!suite) return { items: [], total: 0, filtered: false };
      const { data } = await apiClient.get<CatalogListResponse>(
        `/api/catalog?suite=${encodeURIComponent(suite.apiSuite)}`,
      );
      return data;
    },
    enabled: !!suite,
  });

  if (!suite) {
    return (
      <EmptyState
        icon="🔍"
        title="Suite не найден"
        message={`Suite «${name}» не существует. Доступны: safety, logistics, sales-intelligence.`}
        action={
          <button className="btn-primary" onClick={() => navigate("/")}>
            На главную
          </button>
        }
      />
    );
  }

  return (
    <article className="suite-landing">
      {/* Hero */}
      <header
        className="suite-landing__hero"
        style={{
          background: `linear-gradient(135deg, ${suite.accentColor}22 0%, ${suite.accentColor}08 100%)`,
        }}
      >
        <div className="suite-landing__hero-content">
          <span
            className="suite-landing__suite-pill"
            style={{ backgroundColor: suite.accentColor }}
          >
            {suite.label}
          </span>
          <h1 className="suite-landing__title">{suite.heroTitle}</h1>
          <p className="suite-landing__subtitle">{suite.heroSubtitle}</p>

          <div className="suite-landing__ctas">
            <Link
              to="/request-demo"
              className="btn-primary"
              style={{ backgroundColor: suite.accentColor }}
            >
              Запросить демонстрацию →
            </Link>
            <Link to="/login" className="btn-secondary">
              У меня есть invite
            </Link>
          </div>
        </div>
      </header>

      {/* Value propositions */}
      <section className="suite-landing__section">
        <h2>Ключевая ценность</h2>
        <ul className="suite-landing__value-list">
          {suite.valueProps.map((vp, idx) => (
            <li key={idx} className="suite-landing__value-item">
              <span
                className="suite-landing__value-bullet"
                style={{ backgroundColor: suite.accentColor }}
                aria-hidden="true"
              >
                ✓
              </span>
              <span>{vp}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Description */}
      <section className="suite-landing__section">
        <h2>О суите</h2>
        <p className="suite-landing__description">{suite.description}</p>
      </section>

      {/* Use cases */}
      <section className="suite-landing__section">
        <h2>Реальные сценарии применения</h2>
        <div className="suite-landing__use-cases">
          {suite.useCases.map((uc, idx) => (
            <article key={idx} className="use-case-card">
              <header className="use-case-card__header">
                <span
                  className="use-case-card__industry"
                  style={{ color: suite.accentColor }}
                >
                  {uc.industry}
                </span>
              </header>
              <p className="use-case-card__scenario">{uc.scenario}</p>
              <div
                className="use-case-card__metric"
                style={{ borderColor: suite.accentColor }}
              >
                <span className="use-case-card__metric-label">Результат:</span>
                <span className="use-case-card__metric-value">{uc.metric}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Product list */}
      <section className="suite-landing__section">
        <h2>Продукты в составе суита</h2>
        {productsQuery.isLoading && <CatalogSkeleton count={3} />}
        {productsQuery.error && (
          <p className="suite-landing__error">
            Не удалось загрузить продукты. Попробуйте обновить страницу.
          </p>
        )}
        {productsQuery.data && productsQuery.data.items.length > 0 && (
          <div className="suite-landing__products">
            {productsQuery.data.items.map((entry) => (
              <ProductCard key={entry.slug} entry={entry} />
            ))}
          </div>
        )}
        {productsQuery.data && productsQuery.data.items.length === 0 && (
          <p className="suite-landing__empty">
            Продукты этого суита пока не добавлены в каталог.
          </p>
        )}
      </section>

      {/* Testimonials filtered by suite */}
      <Testimonials
        filterSuite={suite.slug}
        title={`Что говорят клиенты ${suite.label}`}
      />

      {/* Bottom CTA */}
      <footer className="suite-landing__bottom-cta">
        <h3>Готовы обсудить, как {suite.label} решит ваши задачи?</h3>
        <div className="suite-landing__bottom-ctas">
          <Link
            to="/request-demo"
            className="btn-primary"
            style={{ backgroundColor: suite.accentColor }}
          >
            Запросить персональную демонстрацию
          </Link>
          <Link to="/" className="btn-secondary">
            Все продукты IT-Navigate
          </Link>
        </div>
      </footer>
    </article>
  );
}
